import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

export function applyBoldHeader(line: string): string {
  // Special case: numbered item (e.g. "1. Some Header: rest" or "1. Some Header. rest")
  // if (line.includes("Alignment")) {
  //   debugger;
  // }
  if (/^\d+\./.test(line)) {
    // It starts with `${num}.`
    // We want to bold this: `${num}. ${header}:` or `${num}. ${header}.` or `${num}. ${header}` (full line if no colon or period)
    // There may or may not be a space after the . in `${num}.` 
    // If it's not there, let's add it.
    const period = line.indexOf(".");
    if (period > 0) {
      const num = line.substring(0, period);
      let rest = line.substring(period + 1).trim();
      let index = rest.indexOf(":"); 
      if (index < 1) {
        index = rest.indexOf(".");
        index = rest.indexOf(".", index + 1);
      }
      if (index > 0) {
        const bold_me = rest.substring(0, index + 1);
        rest = rest.substring(index + 1);
        return `<b>${num}. ${bold_me}</b>${rest}`;
      }
      return `<b>${num}. ${rest}</b>`;
    }
    return `<b>${line}</b>`;
  }

  // Original logic
  const match = line.match(/^((?:\S+\s+){0,3}\S+\.)(\s+.*)?$/);
  if (match) {
    const candidate = match[1];
    const rest = match[2] ?? "";
    const wordCount = candidate.replace(/\.$/, "").trim().split(/\s+/).length;
    return wordCount <= 4 ? `<b>${candidate}</b>${rest}` : line;
  } else if (line.length < 30) {
    return `<b>${line}</b>`;
  }
  return line;
}

/**
 * Determines whether a line break between two lines is a real paragraph break
 * or a soft wrap that should be joined with a space.
 */
function isRealBreak(before: string, after: string): boolean {
  if (!before || !after) return true;

  // ← add this: a definition label line always starts a new line
  if (/^<b>[^<]+:<\/b>/.test(after.trimStart())) return true;

  if (before.trim().endsWith(":")) {
    return true;
  }
  const beforeWords = before.split(" ");
  if (beforeWords.length < 4) {
    return true;
  }
  if (after.includes("Equipment") || after.includes("•")) {
    console.log(after);
  }
  const afterWords = after.split(" ");
  const endsWithSentence = /[.!?]\s*$/.test(before);
  if (endsWithSentence) {
    // Real break: next line looks like a numbered entry "1." or "1. Word"
    const nextIsNumberedEntry = /^\d+\./.test(after);
    if (nextIsNumberedEntry) {
      return true;
    }
    const nextWord = afterWords[0];
    if (!nextWord || before.length + nextWord.length < 52) {
      return true;
    } else if (afterWords.length === 1) {
      return true;
    }
  } else if (afterWords.length === 1 || before.length < 26) {
    return true;
  }
  return false;
}
// function isRealBreakOld(before: string, after: string): boolean {
//   if (!before || !after) return true;
//   // Real break: ends with sentence-ending punctuation AND next starts new sentence
//   const endsWithSentence = /[.!?]\s*$/.test(before);
//   const nextStartsNew = /^[A-Z0-9]/.test(after);
//   // Real break: next line looks like a numbered entry "1." or "1. Word"
//   const nextIsNumberedEntry = /^\d+\./.test(after);
//   return (endsWithSentence && nextStartsNew) || nextIsNumberedEntry;
// }

/**
 * Detects a "definition list" block: a short standalone header line followed
 * by one or more `Label: value` lines, where values may be soft-wrapped.
 *
 * e.g.
 *   Hit Points
 *   Hit Dice: 1d10 per paladin level
 *   Hit Points at 1st Level: 10 + your Constitution
 *   modifier
 *   Hit Points at Higher Levels: 1d10 (or 6) + your
 *   Constitution modifier per paladin level after 1st
 *
 * becomes:
 *   <b>Hit Points</b>
 *   <b>Hit Dice:</b> 1d10 per paladin level
 *   <b>Hit Points at 1st Level:</b> 10 + your Constitution modifier
 *   <b>Hit Points at Higher Levels:</b> 1d10 (or 6) + your Constitution modifier per paladin level after 1st
 */
function isDefinitionLabel(line: string): boolean {
  // Matches lines like "Hit Dice:", "Hit Points at 1st Level:", "Armor:", "Saving Throws:"
  // Short-ish (under ~60 chars), title-cased-ish, ends with colon
  return /^[A-Z][^:\n]{1,50}:\s*.+$/.test(line) || /^[A-Z][^:\n]{1,50}:$/.test(line);
}

function formatDefinitionLabel(line: string): string {
  const colonIdx = line.indexOf(":");
  if (colonIdx < 0) return line;
  const label = line.substring(0, colonIdx + 1);
  const rest = line.substring(colonIdx + 1);
  return `<b>${label}</b>${rest}`;
}

/**
 * Pre-processes raw page text to:
 * 1. Rejoin soft-wrapped continuations of definition-list values
 * 2. Bold the label portion of each definition line
 *
 * A "continuation" line is one that doesn't start with a capital+colon pattern
 * and isn't blank, occurring immediately after a definition label line.
 */
export function resolveDefinitionLists(text: string): string {
  const lines = text.split("\n");
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trimEnd();

    if (isDefinitionLabel(line)) {
      // Rejoin any soft-wrapped continuations
      let joined = line;
      while (i + 1 < lines.length) {
        const next = lines[i + 1].trimEnd();
        // Stop if blank, or next line is itself a definition label, or looks like a new section header
        if (next === "" || isDefinitionLabel(next) || isSectionHeader(next)) break;
        // Stop if it looks like a bullet or table marker
        if (/^[•\[]/.test(next.trimStart())) break;
        joined = joined.trimEnd() + " " + next.trimStart();
        i++;
      }
      out.push(formatDefinitionLabel(joined));
    } else {
      out.push(line);
    }

    i++;
  }

  return out.join("\n");
}

/**
 * Joins soft-wrapped lines (single \n) that belong to the same paragraph,
 * then collapses double newlines that aren't real paragraph breaks.
 */
function cleanLineBreaks(text: string): string {
  // Step 1: process single newlines — join wrapped lines into paragraphs
  const singleLines = text.split("\n");
  const paragraphLines: string[] = [];

  let prev = "";
  for (let i = 0; i < singleLines.length; i++) {
    const line = singleLines[i].trimEnd();
    const next = singleLines[i + 1]?.trimStart();
    if (line.includes("•") || line.includes("Equipment")) {
      console.log(line);
    }

    // if (next && next.includes("15.")) {
    //   debugger;
    // }
    if (next === undefined) {
      paragraphLines.push(line);
      continue;
    }

    if (line === "") {
      paragraphLines.push(line);
      continue;
    }

    if (prev !== "" && isRealBreak(prev, next)) {
      paragraphLines.push(line);
      prev = "";
    } else {
      const beforeWords = line.split(" ");
      // ← add this: never fold a definition label line forward
      if (/^<b>[^<]+:<\/b>/.test(line)) {
        paragraphLines.push(line);
        prev = line;
      } else if (beforeWords.length < 4 && beforeWords[0][0].toUpperCase() === beforeWords[0][0]) {
        singleLines[i + 1] = "<b>" + line + "</b>\n" + next;
      } else {
        if (next.startsWith("•")) {
          singleLines[i + 1] = line + "\n" + next;
        } else {
          singleLines[i + 1] = line + " " + next;
        }
      }
      prev = next;
    }
  }

  // Step 2: collapse runs of blank lines, apply bold headers only to
  // fully-joined standalone lines, then rejoin
  const result: string[] = [];
  // let pendingBlank = false;

  // debugger;
  for (const line of paragraphLines) {
    if (line.includes("•")) {
      console.log(line);
    }
    if (line === "") {
      // pendingBlank = true;
    } else {
      // Only apply bold header if this line is standalone (preceded by a blank
      // or is the first line) — not a fragment that was joined mid-paragraph
      result.push(applyBoldHeader(line));
    }
  }

  return result.join("\n\n");
}

export function cleanDescription(text: string): string {
  const withDefLists = resolveDefinitionLists(text);
  
  // Split into chunks, keeping definition label lines isolated so
  // cleanLineBreaks doesn't introduce extra blank lines around them
  const lines = withDefLists.split("\n");
  const chunks: { isDefLine: boolean; lines: string[] }[] = [];
  let current: { isDefLine: boolean; lines: string[] } | null = null;

  for (const line of lines) {
    const isDefLine = /^<b>[^<]+:<\/b>/.test(line.trimStart());
    if (!current || current.isDefLine !== isDefLine) {
      current = { isDefLine, lines: [] };
      chunks.push(current);
    }
    current.lines.push(line);
  }

  const processed = chunks.map(chunk => {
    if (chunk.isDefLine) {
      // Already formatted — just join with single newlines, no cleanLineBreaks
      return chunk.lines.join("\n");
    }
    return cleanLineBreaks(chunk.lines.join("\n"));
  });

  const result = processed.join("\n");
  return common_util.normalizeDashes(result);
}

export function mergeAndClean(pieces: string[]): string {
  // debugger;
  return cleanDescription(pieces.join(BOUNDARY));
}

export type ParsedPage  = { page: number; text: string; };
export type ParseResult = { total_pages: number; parsed_pages: number; start_page: number; end_page: number; pages: ParsedPage[]; };
export type Status      = "idle" | "uploading" | "counting" | "parsing" | "done" | "error";
export type ExtractedObject = { id: string; fields: Record<string, string>; };

export function makeId() { return Math.random().toString(36).slice(2, 9); }
export function emptyObject(): ExtractedObject { return { id: makeId(), fields: {} }; }

export function fieldLabel(field: string): string {
  return field.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

export const BASE_URL = "https://wf1migcpd7.execute-api.us-west-2.amazonaws.com/dev";
export const PRESIGN_URL = `${BASE_URL}/getPresignedUrl`;
export const PARSE_URL = `${BASE_URL}/parsePdf`;
export const BUCKET = "hbldev-dev";
export const PAGES_PER_BATCH = 5;

// ─── Repeated Line Detection ─────────────────────────────────────────────────

function normalizePageNumber(line: string): string {
  return line
    .replace(/^\d+\s*[-–—|·]?\s*/, "")
    .replace(/\s*[-–—|·]?\s*\d+$/, "")
    .trim();
}

function isPurePageNumber(line: string): boolean {
  return /^\d+$/.test(line.trim());
}

export type RepeatedLineCandidate = {
  template: string;
  lines: string[];
  count: number;
  fraction: number;
  hasPageNumbers: boolean;
};

const COMMON_REPEATED_LINES = ["Not for resale.", "Permission granted to print or photocopy this document", "for personal use only.", "Permission granted to print or photocopy this document for personal use only."];

export function detectRepeatedLines(pages: ParsedPage[], minFraction = 0.4): RepeatedLineCandidate[] {
  if (pages.length < 2) return [];

  const templateFreq = new Map<string, number>();
  const templateLines = new Map<string, Set<string>>();

  for (const p of pages) {
    const seenTemplates = new Set<string>();
    for (const raw of p.text.split("\n")) {
      const line = raw.trim();
      if (!line) continue;
      const template = isPurePageNumber(line) ? "" : normalizePageNumber(line);
      if (!seenTemplates.has(template)) {
        seenTemplates.add(template);
        templateFreq.set(template, (templateFreq.get(template) ?? 0) + 1);
      }
      if (!templateLines.has(template)) templateLines.set(template, new Set());
      templateLines.get(template)!.add(line);
    }
  }

  const isCommonRepeatedLine = (lines: Set<string>): boolean =>
    Array.from(lines).some(line =>
      COMMON_REPEATED_LINES.some(common => line.includes(common))
    );

  return Array.from(templateFreq.entries())
    .filter(([template, count]) => {
      if (count / pages.length >= minFraction) return true;
      const lines = templateLines.get(template) ?? new Set<string>();
      return isCommonRepeatedLine(lines);
    })
    .map(([template, count]) => {
      const lines = Array.from(templateLines.get(template) ?? []);
      const hasPageNumbers = template !== lines[0] || isPurePageNumber(lines[0]);
      return {
        template: template === "" ? "(page numbers)" : template,
        lines,
        count,
        fraction: count / pages.length,
        hasPageNumbers,
      };
    })
    .sort((a, b) => b.count - a.count);
}

export function stripLinesFromPages(pages: ParsedPage[], removedLines: Set<string>): ParsedPage[] {
  return pages.map(p => {
    const cleaned = p.text
      .split("\n")
      .filter(raw => !removedLines.has(raw.trim()))
      .join("\n")
      .replace(/\n{3,}/g, "\n\n");
    return { ...p, text: cleaned };
  });
}

// ─── Auto Section Generation ─────────────────────────────────────────────────

const SECTION_HEADER_MAX_WORDS = 4;

function isSectionHeader(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (/^[\d\s\-–—•*]+$/.test(trimmed)) return false;
  if (/^[a-z]/.test(trimmed)) return false;
  const words = trimmed.split(/\s+/).filter(Boolean);
  return words.length >= 1 && words.length <= SECTION_HEADER_MAX_WORDS;
}


// Sentinel used to mark where a page/column boundary fell inside a section.
// Must not appear in real content.
const BOUNDARY = "\x00BOUNDARY\x00";

/**
 * Joins lines that were split across a page or column boundary but belong to
 * the same paragraph. Works the same way cleanLineBreaks2 does internally:
 * if the line before the boundary doesn't end with a period, and the line
 * after doesn't start a new entry, collapse the boundary into a space.
 */
function resolveBoundaries(text: string): string {
  // Split on the sentinel, preserving context around each boundary
  const parts = text.split(BOUNDARY);
  // if (text.includes("15.")) {
  //   debugger;
  // }
  if (parts.length === 1) return text;

  let result = parts[0];
  for (let i = 1; i < parts.length; i++) {
    const before = result.trimEnd();
    const after = parts[i].trimStart();

    const endsWithPeriod = /\.\s*$/.test(before);
    const nextIsNewEntry = /^[A-Z][^.]+\./.test(after);

    if (!endsWithPeriod && !nextIsNewEntry && before.length > 0 && after.length > 0) {
      result = before + " " + after;
    } else {
      result = before + "\n" + after;
    }
  }
  return result;
}

export function autoGenerateSections(pages: ParsedPage[]): ExtractedObject[] {
  const sections: ExtractedObject[] = [];
  let current: ExtractedObject | null = null;
  const contentLines: string[] = [];

  const flush = () => {
    if (!current) return;
    const raw = resolveBoundaries(contentLines.join("\n"));
    current.fields["content"] = cleanDescription(raw).trim(); 
    if (current.fields["content"] || current.fields["name"]) {
      sections.push(current);
    }
    current = null;
    contentLines.length = 0;
  };

  for (let pi = 0; pi < pages.length; pi++) {
    const page = pages[pi];
    const lines = page.text.split("\n").map(l => l.trimEnd()); // injectTablesIntoLines(page.text.split("\n").map(l => l.trimEnd()), page.tables);

    let can_be_header = true;
    for (let li = 0; li < lines.length; li++) {
      const line = lines[li];
      if (line.includes("Subraces")) {
        debugger;
      }
      if (can_be_header && isSectionHeader(line)) {
        flush();
        current = emptyObject();
        current.fields["name"] = line.trim();
      } else if (current) {
        // Insert a boundary sentinel at page/column edges (blank lines that
        // fall at the very start or end of a page's contribution to this section)
        const isFirstContentLine = contentLines.length === 0 || contentLines[contentLines.length - 1] === BOUNDARY;
        if (line === "" && (li === lines.length - 1 || isFirstContentLine)) {
          // Trailing blank on this page or leading blank — mark as boundary
          if (!isFirstContentLine) contentLines.push(BOUNDARY);
        } else {
          contentLines.push(line);
          // if (line.length > 0) {
          //   can_be_header = line.endsWith(".");
          // }
          can_be_header = line.length === 0 || line.endsWith(".");
        }
      }
    }

    // After each page (except the last), insert a boundary if we're mid-section
    if (current && pi < pages.length - 1) {
      const last = contentLines[contentLines.length - 1];
      if (last !== undefined && last !== BOUNDARY && last !== "") {
        contentLines.push(BOUNDARY);
      }
    }
  }
  flush();
  return sections;
}